import { useSignal } from '@preact/signals';
import { useEffect } from 'preact/hooks';

import { qrPath, type QrModules } from '../lib/qrPath.js';
import styles from './guide.module.css';

// QR-code in de browser (golf 6a, #13): gemaakt met `qrcode` (MIT) in deze pagina, nooit via een
// externe dienst, zodat het token van een QR-koppeling de ouderapp niet verlaat. De bibliotheek
// laadt pas als er een QR-code getoond wordt (eigen chunk). Het resultaat is één SVG-pad per
// module: geen innerHTML, alleen getallen.

/** Maakt de modules van een QR-code (foutcorrectie M: goed leesbaar op een scherm). */
export async function qrModules(value: string): Promise<QrModules> {
  const lib = await import('qrcode');
  const qr = lib.create(value, { errorCorrectionLevel: 'M' });
  return { size: qr.modules.size, data: qr.modules.data };
}

export interface QrCodeProps {
  value: string;
  /** Toegankelijke naam, bv. "QR-code om een tablet te koppelen in Woonkamer". */
  label: string;
  /** Weergavemaat in CSS-pixels (vierkant). */
  size?: number;
  name?: string;
}

/** De QR-code als SVG (zwart op wit, ook in een donker thema: camera's lezen dat het best). */
export function QrCode({ value, label, size = 224, name }: QrCodeProps) {
  const modules = useSignal<QrModules | null>(null);
  const failed = useSignal(false);

  useEffect(() => {
    let alive = true;
    modules.value = null;
    failed.value = false;
    qrModules(value)
      .then((m) => {
        if (alive) modules.value = m;
      })
      .catch(() => {
        if (alive) failed.value = true;
      });
    return () => {
      alive = false;
    };
  }, [value]);

  const margin = 4;
  const m = modules.value;
  const total = m ? m.size + margin * 2 : 1;
  return (
    <div
      class={styles.qr}
      style={{ width: `${size}px`, height: `${size}px` }}
      data-name={name}
      data-ready={m ? 'true' : 'false'}
    >
      {m ? (
        <svg
          viewBox={`0 0 ${total} ${total}`}
          width={size}
          height={size}
          role="img"
          aria-label={label}
          shape-rendering="crispEdges"
        >
          <rect width={total} height={total} class={styles.qrPaper} />
          <path d={qrPath(m, margin)} class={styles.qrInk} />
        </svg>
      ) : failed.value ? null : (
        <span class={styles.qrLoading} aria-hidden="true" />
      )}
    </div>
  );
}
